import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BarcodeModal = ({ isOpen, onClose, selectedItems, onGenerate }) => {
  const [barcodeSettings, setBarcodeSettings] = useState({
    format: 'CODE128',
    includeText: true,
    size: 'medium',
    quantity: 1
  });

  const formatOptions = [
    { value: 'CODE128', label: 'CODE128 (Recommended)' },
    { value: 'QR', label: 'QR Code' },
    { value: 'EAN13', label: 'EAN-13' },
    { value: 'CODE39', label: 'CODE39' }
  ];

  const sizeOptions = [
    { value: 'small', label: 'Small (2x1 inch)' },
    { value: 'medium', label: 'Medium (3x1.5 inch)' },
    { value: 'large', label: 'Large (4x2 inch)' }
  ];

  const handleGenerate = () => {
    onGenerate({
      items: selectedItems,
      settings: barcodeSettings
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1001">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="QrCode" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Generate Barcodes</h2>
              <p className="text-sm text-muted-foreground">
                {selectedItems.length} item(s) selected for barcode generation
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        <div className="p-6 space-y-6">
          {/* Barcode Format */}
          <div>
            <Select
              label="Barcode Format"
              description="Choose the barcode format for your labels"
              options={formatOptions}
              value={barcodeSettings.format}
              onChange={(value) => setBarcodeSettings(prev => ({ ...prev, format: value }))}
            />
          </div>

          {/* Label Size */}
          <div>
            <Select
              label="Label Size"
              description="Select the physical size of barcode labels"
              options={sizeOptions}
              value={barcodeSettings.size}
              onChange={(value) => setBarcodeSettings(prev => ({ ...prev, size: value }))}
            />
          </div>

          {/* Quantity per Item */}
          <div>
            <Input
              label="Quantity per Item"
              type="number"
              min="1"
              max="100"
              description="Number of labels to print for each selected item"
              value={barcodeSettings.quantity}
              onChange={(e) => setBarcodeSettings(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
            />
          </div>

          {/* Include Text */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="includeText"
              checked={barcodeSettings.includeText}
              onChange={(e) => setBarcodeSettings(prev => ({ ...prev, includeText: e.target.checked }))}
              className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-primary focus:ring-2"
            />
            <label htmlFor="includeText" className="text-sm font-medium text-foreground">
              Include readable text below barcode
            </label>
          </div>

          {/* Preview Section */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3">Preview</h3>
            <div className="flex items-center justify-center p-6 bg-white rounded border-2 border-dashed border-border">
              <div className="text-center">
                <div className="w-32 h-16 bg-black/80 rounded mb-2 flex items-center justify-center">
                  <div className="text-white text-xs font-mono">||||| |||| |||||</div>
                </div>
                {barcodeSettings.includeText && (
                  <div className="text-xs font-mono text-muted-foreground">NJT-2025-001</div>
                )}
              </div>
            </div>
          </div>

          {/* Selected Items Summary */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3">Selected Items</h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {selectedItems.slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">Floral Print - Size M - Piece #{item}</span>
                  <span className="text-muted-foreground">{barcodeSettings.quantity} labels</span>
                </div>
              ))}
              {selectedItems.length > 5 && (
                <div className="text-sm text-muted-foreground">
                  +{selectedItems.length - 5} more items...
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <div className="text-sm text-muted-foreground">
            Total labels to generate: {selectedItems.length * barcodeSettings.quantity}
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleGenerate}
              iconName="Printer"
              iconPosition="left"
              iconSize={16}
            >
              Generate & Print
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeModal;